/*
 * Description:
 * ---------------------
 * Every sentence the console says about getting in, and about who may: the
 * sign-in screen, a forgotten password, an invitation, setting up a new box,
 * a person's own email, and inviting people and adding boxes under Access
 * Control.
 *
 * The reader may be a small business owner holding a box for the first time,
 * so the words are plain and an organisation is never a "domain". A refusal is
 * the server's own sentence (server/pkg/portalauth), which already names the
 * rule that was broken; these words only frame it.
 *
 * ! file components/accessWords.js
 */

/* What each role may do, in rank order. The keys are the server's. */
export const ROLES = [
  { key: "viewer", label: "Viewer", blurb: "Sees its organisation's boxes but cannot change anything." },
  { key: "operator", label: "Operator", blurb: "Changes the settings of its organisation's boxes, Remote Access included." },
  { key: "tenant_admin", label: "Organisation Admin", blurb: "An operator who also manages the people in its organisation." },
  { key: "super_admin", label: "Super Admin", blurb: "Full control of every organisation, account and box." },
];

export const roleLabel = (role) => ROLES.find((r) => r.key === role)?.label || role || "—";

/* ── Shared ─────────────────────────────────────────────────────────────── */

export const UNREACHABLE = "Could not reach the Weavian server. Check your connection.";
export const BACK = "Back";
export const BACK_TO_SIGN_IN = "Back to sign in";
export const BACK_TO_CONSOLE = "Back to the console";
export const TRY_AGAIN = "Try again";
export const SENDING = "Sending…";
export const SAVING = "Saving…";
export const CREATING = "Creating your account…";
export const CHECK_EMAIL = "Check your email";

/* A server sentence as this console shows one: capitalised, with a full stop. */
export function sentence(s) {
  const t = String(s || "").trim();
  if (!t) return "";
  return `${t[0].toUpperCase()}${t.slice(1)}${/[.!?]$/.test(t) ? "" : "."}`;
}

/* Why a call was refused: the server's own sentence, or its status when it
   gave none. */
export const refusal = ({ status, data }) =>
  sentence(data?.error) || `The Weavian server refused this (${status}).`;

export const atLeast = (n) => `At least ${n} characters`;
export const tooShort = (n) => `Password must be at least ${n} characters.`;
export const MISMATCH = "Passwords do not match.";
export const REENTER = "Re-enter it";
export const USERNAME_HINT = "3 to 32 letters, digits, dots, dashes or underscores.";
export const CODE = "Code from the email";

/* ── Sign in ────────────────────────────────────────────────────────────── */

export const LOGIN_LABEL = "Username or email";
export const FORGOT_LINK = "Forgot your password?";
export const SIGNUP_PROMPT = "Have a box that is not set up yet?";
export const SIGNUP_LINK = "Set up a new box";

/* ── Forgotten password ─────────────────────────────────────────────────── */

export const FORGOT_TITLE = "Forgot your password?";
export const FORGOT_LEAD = "Enter your username or email, and we will email you a link to choose a new password.";
export const FORGOT_SEND = "Email me a link";
export const FORGOT_SENT =
  "If that account has an email, a link to choose a new password is on its way. It works for an hour.";
export const FORGOT_NO_EMAIL = "No email on your account? Ask your administrator to set a new password for you.";

export const RESET_TITLE = "Choose a new password";
export const RESET_LEAD = "Pick a new password for your account. You are signed in as soon as it is saved.";
export const RESET_SAVE = "Save password & sign in";
export const RESET_ENDED_TITLE = "This link has ended";
export const RESET_AGAIN = "Email me a new link";

/* ── Invitation ─────────────────────────────────────────────────────────── */

export const YOUR_INVITATION = "Your invitation";
export const INVITE_OPENING = "Opening your invitation…";
export const inviteTitle = (organisation) => `Join ${organisation}`;
export const inviteLead = ({ by, organisation }) =>
  `${by} invited you to ${organisation} on Weavian. Choose a username and password to finish.`;
export const INVITE_ENDED_TITLE = "This invitation has ended";
export const INVITE_ENDED =
  "It has expired, was already used, or was withdrawn. Ask the person who invited you to send a new one.";
export const INVITE_ACCEPT = "Create my account";

/* ── A new box ──────────────────────────────────────────────────────────── */

export const BOX_CODE = "Box code";
export const BOX_CODE_EXAMPLE = "7KQ2-M9XD-4RTB";
/* Where a customer finds a box's code. The box itself prints it only to its
   own shell (anchor-edge claim-code), so the label is the one place named. */
export const BOX_CODE_WHERE =
  "It is on the label that came with the box: 12 characters, like 7KQ2-M9XD-4RTB. Dashes and capitals do not matter.";

export const SIGNUP_TITLE = "Set up a new box";
export const SIGNUP_LEAD = "Create an account for your organisation and add your box.";
export const ORG = "Organisation name";
export const ORG_EXAMPLE = "Your company or team";
export const YOUR_EMAIL = "Your email";
export const SIGNUP_EMAIL_HINT = "We email you a code to check it is yours. It is also how you reset a forgotten password.";
export const SEND_CODE = "Email me a code";
export const signupCodeSent = (email) =>
  `We emailed a 6-digit code to ${email}. Enter it, then choose your username and password. The code works for 30 minutes.`;
export const SIGNUP_FINISH = "Create account & sign in";

/* ── Your account ───────────────────────────────────────────────────────── */

export const ACCOUNT = "Your account";
export const ACCESS_LEAD =
  "Who can sign in and what each person may do. Each box belongs to one organisation, and people see only their own organisation's boxes.";
export const accountLead = (username, role) => `Signed in as ${username} (${roleLabel(role)}).`;
export const EMAIL_TITLE = "Your email";
export const NO_EMAIL = "No email on your account yet";

/* Why the email matters, or why it cannot be changed here. */
export function emailWhy(hasEmail, sendsEmail) {
  if (!sendsEmail) {
    return "This Weavian server cannot send email yet, so the address cannot be changed here, and only an administrator can reset a forgotten password.";
  }
  return hasEmail
    ? "If you forget your password, a link to choose a new one goes to this address."
    : "Add one so you can choose a new password yourself if you forget it. Without one, only an administrator can reset it.";
}

export const NEW_EMAIL = "New email";
export const CURRENT_PASSWORD = "Your current password";
export const CHANGE_HINT = "We email a code to the new address to check it is yours. Nothing changes until you enter it.";
export const emailCodeSent = (email) =>
  `We emailed a 6-digit code to ${email}. Enter it here to finish. It works for 30 minutes.`;
export const CONFIRM = "Confirm";
export const emailNow = (email) => `Done. Your email is now ${email}.`;

/* ── Access Control ─────────────────────────────────────────────────────── */

export const EMAIL = "Email";
export const ORGANISATION = "Organisation";
export const ORGANISATIONS = "Organisations";
export const PICK_AN_ORG = "Pick at least one organisation, or this account sees nothing.";
export const orgsUpdated = (username) => `${username}'s organisations updated`;
export const ROLE = "Role";
export const userEmail = (u) => u.email || "no email";

export const INVITES_TITLE = "Invitations";
export const INVITES_LEAD =
  "Invite someone by email and they choose their own username and password. An invitation works for 7 days.";
export const INVITE = "Invite by email";
export const INVITE_SEND = "Send invitation";
export const NO_INVITES = "No invitation is waiting for an answer.";
export const INVITES_NO_EMAIL = "This Weavian server cannot send email, so no new invitation can be sent from here.";
export const NO_ORGS = "There is no organisation to add anyone or anything to yet.";
export const invited = (email) => `Invitation sent to ${email}. It works for 7 days.`;
export const inviteMeta = (inv, organisation, until) =>
  `${roleLabel(inv.role)} in ${organisation} · invited by ${inv.by} · works until ${until}`;
export const withdrawLabel = (email) => `Withdraw the invitation to ${email}`;
export const WITHDRAW = "Withdraw";
export const WITHDRAW_MEANS = "The link in it stops working. You can invite them again at any time.";
export const withdrawn = (email) => `The invitation to ${email} is withdrawn.`;

export const CLAIM_TITLE = "Add a box by its code";
export const CLAIM_LEAD = "Enter the code that came with a new box, and it joins the organisation you pick.";
export const CLAIM = "Add box";
export const claimed = (node, organisation) =>
  `${node} now belongs to ${organisation}. It shows up in your boxes within a few seconds.`;

/* ── Laptops and phones ─────────────────────────────────────────────────── */

export const DEVICES_TITLE = "Laptops and phones";
export const DEVICES_LEAD =
  "A laptop joins your organisation with a one-time code. Switch off a laptop or phone that is lost, and it reaches nothing until you switch it on again.";
export const MAKE_CODE = "Make a code";
export const DEVICE_NAME = "Name";
export const DEVICE_NAME_EXAMPLE = "Dana's field laptop";
export const DEVICE_NAME_WHY = "Only you see the name, to tell your codes apart.";
export const MAKE = "Make code";
export const codeFor = (label) => `Code for ${label}`;
export const codeUse = (until) =>
  `Put it in the laptop's configuration as enroll_code. It works once, until ${until}, and is not shown again.`;
export const COPY = "Copy";
export const COPIED = "Code copied.";
export const COPY_FAILED = "Could not copy. Select the code and copy it by hand.";
export const DONE = "Done";
export const CODES_WAITING = "Codes waiting for a laptop";
export const codeMeta = (en, organisation, until) => `${organisation} · made by ${en.by} · works until ${until}`;
export const withdrawCodeLabel = (label) => `Withdraw the code for ${label}`;
export const WITHDRAW_CODE_MEANS = "It stops working. You can make another at any time.";
export const codeWithdrawn = (label) => `The code for ${label} is withdrawn.`;
export const DEVICES_JOINED = "Joined";
export const NO_DEVICES = "No laptop or phone has joined yet.";
export const NO_ORG = "no organisation";
export const SWITCHED_OFF = "switched off";
export const SWITCH_OFF = "Switch off";
export const SWITCH_ON = "Switch on";
export const switchOffLabel = (node) => `Switch off ${node}`;
export const switchOnLabel = (node) => `Switch on ${node}`;
export const SWITCH_OFF_MEANS =
  "It carries nothing through Weavian from then on: its connection stops within about half a minute, and it cannot connect again under this name until you switch it on. A device set up with a shared registration token instead of a one-time code could still join again under another name.";
export const switchedOff = (node) => `${node} is switched off. Its connection stops within about half a minute.`;
export const switchedOn = (node) => `${node} is switched on. It reconnects by itself.`;
export const deviceMeta = (n, organisation, seen, until) =>
  [organisation, n.stale ? `last seen ${seen}` : "online now", n.user && signedIn(n.user, until),
    n.failing?.length > 0 && failingLine(n.failing)]
    .filter(Boolean)
    .join(" · ");

/* ── Sign-in with Microsoft ─────────────────────────────────────────────── */

export const SIGNIN_TITLE = "Sign-in with Microsoft";
export const SIGNIN_LEAD =
  "People sign in on your laptops with the accounts your organisation already has in Microsoft Entra ID, and you see who is using each laptop.";
export const SIGNIN_HOWTO = "How to set it up in Microsoft Entra ID";
export const SIGNIN_STEPS = [
  "In the Microsoft Entra admin center, open App registrations and register a new application called Weavian, for accounts in this organisational directory only.",
  "Under Authentication, add the platform Mobile and desktop applications with the redirect URI http://localhost.",
  "Copy the Application (client) ID and the Directory (tenant) ID from its Overview page into the boxes below.",
  "To give groups of people different access later, add app roles to the application and assign your groups to them.",
];
export const signInFor = (org) => `Sign-in with Microsoft for ${org}`;
export const SIGNIN_ON = "on";
export const SIGNIN_NOT_SET = "not set up";
export const TENANT_ID = "Directory (tenant) ID";
export const CLIENT_ID = "Application (client) ID";
export const SESSION_HOURS = "Hours a sign-in lasts";
export const TURN_ON = "Turn on";
export const TURN_OFF = "Turn off";
export const SAVE = "Save";
export const signInChangeTitle = (org) => `Change sign-in for ${org}?`;
export const signInOffTitle = (org) => `Turn off sign-in for ${org}?`;
export const SIGNIN_CHANGE_MEANS = "Everyone signed in on its laptops is signed out, and signs in again.";
export const signInOn = (org) => `People sign in on ${org}'s laptops with their Microsoft accounts.`;
export const signInOff = (org) => `Sign-in with Microsoft is off for ${org}.`;
export const signedIn = (u, until) => `${u.name || u.email} signed in until ${until}`;

/* ── What skips the tunnel ──────────────────────────────────────────────── */

export const SKIP_TITLE = "What skips the tunnel";
export const SKIP_LEAD =
  "Everything a laptop sends goes through Weavian, except what you pick here, which goes straight out of whichever of its own networks is working.";
export const skipFor = (org) => `What skips the tunnel for ${org}`;
export const ADD_SKIP = "Add a network";
export const removeSkip = (n) => `Stop ${n} skipping the tunnel`;
export const SKIP_NOTE =
  "What skips the tunnel is not encrypted by Weavian and leaves from that network's own address. When that network drops, it moves to another within seconds, but its connections start over. Laptops take a change within a minute.";
export const skipSaved = (org) => `${org}'s laptops take the change within a minute.`;

/* ── Device checks ──────────────────────────────────────────────────────── */

export const CHECKS_TITLE = "Device checks";
export const CHECKS_LEAD =
  "What a laptop must show of itself to reach anything inside your organisation. One that fails a check is told which, and reaches nothing inside until it passes.";
export const checksFor = (org) => `Device checks for ${org}`;
export const CHECK_DISK = "Disk encrypted with BitLocker";
export const CHECK_UPDATES = "Windows updated within";
export const CHECK_DAYS = "Days since Windows last installed updates";
export const DAYS = "days";
export const CHECKS_NOTE =
  "Laptops report themselves every hour. One that has not reported for a day fails every check asked.";
export const checksSaved = (org) => `${org}'s laptops are held to these checks from their next report.`;
export const FAILING_WORDS = {
  disk_encrypted: "BitLocker off",
  updated: "Windows not updated lately",
};
export const failingLine = (failing) =>
  `fails ${failing.map((f) => FAILING_WORDS[f] || f).join(" and ")}`;
